import { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Switch,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Badge,
  Separator,
} from '../shadcn';
import { AnimatePresence, motion } from 'framer-motion';
import {
  shouldShowBanner,
  getConsent,
  saveConsent,
  acceptAllConsent,
  acceptNecessaryOnly,
  OPEN_CONSENT_EVENT,
  type ConsentState,
} from '../../lib/consent';
import { useTranslation } from 'react-i18next';

interface ConsentOption {
  id: 'necessary' | 'analytics' | 'marketing';
  title: string;
  description: string;
  required?: boolean;
}

export function CookieBanner() {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [consent, setConsent] = useState<ConsentState>({
    necessary: true,
    analytics: false,
    marketing: false,
  });
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const currentConsent = getConsent();
    if (currentConsent) {
      setConsent(currentConsent);
    }
    setShowBanner(shouldShowBanner());
  }, []);

  useEffect(() => {
    const openPreferences = () => {
      const currentConsent = getConsent();
      if (currentConsent) {
        setConsent(currentConsent);
      }
      setShowDetails(false);
      setShowBanner(true);
    };
    window.addEventListener(OPEN_CONSENT_EVENT, openPreferences);
    return () =>
      window.removeEventListener(OPEN_CONSENT_EVENT, openPreferences);
  }, []);

  const consentOptions: ConsentOption[] = [
    {
      id: 'necessary',
      title: t('common.consent.necessary.title'),
      description: t('common.consent.necessary.description'),
      required: true,
    },
    {
      id: 'analytics',
      title: t('common.consent.analytics.title'),
      description: t('common.consent.analytics.description'),
    },
    {
      id: 'marketing',
      title: t('common.consent.marketing.title'),
      description: t('common.consent.marketing.description'),
    },
  ];

  const handleConsentChange = (
    optionId: 'necessary' | 'analytics' | 'marketing',
    enabled: boolean
  ) => {
    if (optionId === 'necessary') return;

    setConsent((prev) => ({
      ...prev,
      [optionId]: enabled,
    }));
  };

  const handleAcceptAll = () => {
    const newConsent = acceptAllConsent();
    setConsent(newConsent);
    setShowBanner(false);
  };

  const handleReject = () => {
    const newConsent = acceptNecessaryOnly();
    setConsent(newConsent);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    saveConsent(consent);
    setShowBanner(false);
    setShowDetails(false);
  };

  if (!showBanner) {
    return null;
  }

  const choiceButtonClass = 'flex-1 h-11 text-base font-semibold';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4"
        role="dialog"
        aria-modal="false"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
      >
        <Card className="mx-auto max-w-4xl border-2 shadow-lg backdrop-blur-sm bg-background/95">
          <CardHeader className="pb-4">
            <CardTitle id="cookie-consent-title" className="text-lg">
              {t('common.consent.title')}
            </CardTitle>
            <CardDescription
              id="cookie-consent-description"
              className="text-sm"
            >
              {t('common.consent.description')}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                onClick={handleReject}
                variant="outline"
                className={choiceButtonClass}
              >
                {t('common.consent.reject')}
              </Button>

              <Button
                type="button"
                onClick={handleAcceptAll}
                variant="outline"
                className={choiceButtonClass}
              >
                {t('common.consent.accept')}
              </Button>
            </div>

            <Dialog open={showDetails} onOpenChange={setShowDetails}>
              <DialogTrigger asChild>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                >
                  {t('common.consent.customize')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('common.consent.dialogTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('common.consent.dialogDescription')}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                  {consentOptions.map((option, index) => (
                    <div key={option.id}>
                      <div className="flex items-start justify-between space-x-4">
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium">
                              {option.title}
                            </h4>
                            {option.required && (
                              <Badge variant="secondary" className="text-xs">
                                {t('common.consent.required')}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {option.description}
                          </p>
                        </div>
                        <Switch
                          checked={consent[option.id]}
                          onCheckedChange={(checked) =>
                            !option.required &&
                            handleConsentChange(option.id, checked)
                          }
                          disabled={option.required}
                          aria-label={option.title}
                        />
                      </div>
                      {index < consentOptions.length - 1 && (
                        <Separator className="mt-4" />
                      )}
                    </div>
                  ))}

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowDetails(false)}
                    >
                      {t('common.consent.cancel')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSavePreferences}
                    >
                      {t('common.consent.save')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}
